import { lazy, Suspense, useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import type { JSX } from 'react';
import type { PromptDelivery, SessionMode } from '@aasis21/weft-shared';
import { LandingScreen } from '@/ui/screens/LandingScreen';
import { ConnectScreen } from '@/ui/screens/ConnectScreen';
import type { StartMode } from '@/ui/screens/StartSessionScreen';
import { isNativeRuntime } from '@/ui/hooks/usePairing';
import { sessionAccess, sessionRuntime } from '@/session/runtime/instance';
import {
  lifecycleFailureMessage,
  requiresTakeoverConfirmation,
} from '@/session/access/sessionAccess';

const StartSessionScreen = lazy(() =>
  import('@/ui/screens/StartSessionScreen').then((module) => ({ default: module.StartSessionScreen })),
);
const DevicesScreen = lazy(() =>
  import('@/ui/screens/DevicesScreen').then((module) => ({ default: module.DevicesScreen })),
);
const DeviceDetailsScreen = lazy(() =>
  import('@/ui/screens/DeviceDetailsScreen').then((module) => ({ default: module.DeviceDetailsScreen })),
);
const SessionScreen = lazy(() =>
  import('@/ui/screens/SessionScreen').then((module) => ({ default: module.SessionScreen })),
);
const TerminalScreen = lazy(() =>
  import('@/ui/screens/TerminalScreen').then((module) => ({ default: module.TerminalScreen })),
);

type ModalHistoryState = { weftView: 'devices' } |
  { weftView: 'device-details' | 'terminal'; channelId: string } |
  { weftView: 'explore'; exploreView?: string; entry?: 'direct-discover' } | null;

function loadingScreen(label: string): JSX.Element {
  return (
    <main className="boot" role="status" aria-live="polite">
      <div className="boot-mark" aria-hidden="true">W</div>
      <p>{label}</p>
    </main>
  );
}

export default function App(): JSX.Element {
  const snapshot = useSyncExternalStore(sessionRuntime.subscribe, sessionRuntime.getSnapshot);
  const [adding, setAdding] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startDeviceId, setStartDeviceId] = useState<string | undefined>(undefined);
  /** Which tab the start screen opens on — "Resume a session" on a device lands straight on the
   *  resumable list rather than making you find the tab yourself. */
  const [startMode, setStartMode] = useState<StartMode>('new');
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [deviceDetailsChannelId, setDeviceDetailsChannelId] = useState<string | undefined>(undefined);
  const [terminalChannelId, setTerminalChannelId] = useState<string | undefined>();
  const [exploreOpen, setExploreOpen] = useState(false);
  const [exploreEntry, setExploreEntry] = useState<'home' | 'direct-discover'>('home');
  const [addManual, setAddManual] = useState(false);
  const [showLanding, setShowLanding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const removeSession = useCallback((channelId: string): void => {
    const operationId = snapshot.sessions.find((session) => session.meta.channelId === channelId)
      ?.spawning?.requestId;
    void (operationId ? sessionAccess.cancel(operationId) : sessionRuntime.remove(channelId));
  }, [snapshot.sessions]);

  useEffect(() => {
    void sessionRuntime.init().then(() => sessionAccess.init());
  }, []);

  // Devices / Device details have no dedicated in-page back button — the phone's own Back
  // gesture (or the browser Back button on web) closes them, via history entries we push when
  // opening each one. `null` state means "neither is open" (whatever screen would render below).
  useEffect(() => {
    const onPopState = (event: PopStateEvent): void => {
      const state = event.state as ModalHistoryState;
      setExploreOpen(state?.weftView === 'explore');
      setExploreEntry(
        state?.weftView === 'explore' && state.entry === 'direct-discover'
          ? 'direct-discover'
          : 'home',
      );
      setTerminalChannelId(state?.weftView === 'terminal' ? state.channelId : undefined);
      if (state?.weftView === 'devices') {
        setDevicesOpen(true);
        setDeviceDetailsChannelId(undefined);
      } else if (state?.weftView === 'device-details') {
        setDevicesOpen(false);
        setDeviceDetailsChannelId(state.channelId);
      } else {
        setDevicesOpen(false);
        setDeviceDetailsChannelId(undefined);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const openJoin = useCallback((manual = false): void => {
    setExploreOpen(false);
    setTerminalChannelId(undefined);
    window.history.replaceState(null, '');
    setError(null);
    setAddManual(manual);
    setStarting(false);
    setDevicesOpen(false);
    setDeviceDetailsChannelId(undefined);
    setAdding(true);
  }, []);

  const openStart = useCallback((channelId?: string, mode: StartMode = 'new'): void => {
    setExploreOpen(false);
    setTerminalChannelId(undefined);
    window.history.replaceState(null, '');
    setError(null);
    setAdding(false);
    setDevicesOpen(false);
    setDeviceDetailsChannelId(undefined);
    setStartDeviceId(channelId);
    setStartMode(mode);
    setStarting(true);
  }, []);

  const openDevices = useCallback((): void => {
    setExploreOpen(false);
    setTerminalChannelId(undefined);
    setError(null);
    setAdding(false);
    setStarting(false);
    setDeviceDetailsChannelId(undefined);
    setDevicesOpen(true);
    window.history.pushState({ weftView: 'devices' } satisfies ModalHistoryState, '');
  }, []);

  const openDeviceDetails = useCallback((channelId: string): void => {
    setExploreOpen(false);
    setTerminalChannelId(undefined);
    setError(null);
    setAdding(false);
    setStarting(false);
    setDevicesOpen(false);
    setDeviceDetailsChannelId(channelId);
    window.history.pushState({ weftView: 'device-details', channelId } satisfies ModalHistoryState, '');
  }, []);
  const openTerminal = useCallback((channelId: string): void => {
    setExploreOpen(false);
    const terminal = sessionRuntime.terminal(channelId);
    terminal.enter();
    terminal.open();
    setTerminalChannelId(channelId);
    setDeviceDetailsChannelId(undefined);
    setDevicesOpen(false);
    window.history.pushState({ weftView: 'terminal', channelId } satisfies ModalHistoryState, '');
  }, []);
  const startDeviceMonitoring = useCallback((channelId: string): void => {
    sessionRuntime.startDeviceMonitoring(channelId);
  }, []);
  const stopDeviceMonitoring = useCallback((channelId: string): void => {
    sessionRuntime.stopDeviceMonitoring(channelId);
  }, []);
  const openDeviceClipboard = useCallback((channelId: string): void => {
    sessionRuntime.openDeviceClipboard(channelId);
  }, []);
  const closeDeviceClipboard = useCallback((channelId: string): void => {
    sessionRuntime.closeDeviceClipboard(channelId);
  }, []);
  const readDeviceClipboard = useCallback((channelId: string): void => {
    sessionRuntime.readDeviceClipboard(channelId);
  }, []);
  const writeDeviceClipboard = useCallback((channelId: string, text: string): void => {
    sessionRuntime.writeDeviceClipboard(channelId, text);
  }, []);
  const refreshDeviceKeepAwake = useCallback((channelId: string): void => {
    sessionRuntime.refreshDeviceKeepAwake(channelId);
  }, []);
  const startDeviceKeepAwake = useCallback((channelId: string, durationMs: number): void => {
    sessionRuntime.startDeviceKeepAwake(channelId, durationMs);
  }, []);
  const stopDeviceKeepAwake = useCallback((channelId: string): void => {
    sessionRuntime.stopDeviceKeepAwake(channelId);
  }, []);

  const handlePair = useCallback(async (raw: string): Promise<void> => {
    const route = await sessionRuntime.addByQr(raw);
    setAdding(false);
    setShowLanding(false);
    if (route.startsWith('listener:')) {
      openDeviceDetails(route.slice('listener:'.length));
    }
  }, [openDeviceDetails]);

  const closeDeviceScreens = useCallback((): void => {
    setTerminalChannelId(undefined);
    window.history.replaceState(null, '');
    setDevicesOpen(false);
    setDeviceDetailsChannelId(undefined);
  }, []);

  const openExplore = useCallback((): void => {
    setError(null);
    setAdding(false);
    setStarting(false);
    setDevicesOpen(false);
    setDeviceDetailsChannelId(undefined);
    setTerminalChannelId(undefined);
    setExploreEntry('home');
    setExploreOpen(true);
    window.history.pushState({ weftView: 'explore' } satisfies ModalHistoryState, '');
  }, []);

  const openDiscoverFromChat = useCallback((): void => {
    setError(null);
    setExploreEntry('direct-discover');
    setExploreOpen(true);
    window.history.pushState({
      weftView: 'explore',
      exploreView: 'discover',
      entry: 'direct-discover',
    } satisfies ModalHistoryState, '');
  }, []);

  const closeExplore = useCallback((): void => {
    const state = window.history.state as ModalHistoryState;
    window.history.go(
      state?.weftView === 'explore' &&
      state.exploreView &&
      state.entry !== 'direct-discover'
        ? -2
        : -1,
    );
  }, []);

  const handleVoiceModeChange = useCallback((channelId: string, active: boolean): void => {
    void sessionRuntime.setVoiceMode(active, channelId);
  }, []);

  const handleDemo = useCallback(async (): Promise<void> => {
    window.history.replaceState(null, '');
    await sessionRuntime.addDemo();
    setAdding(false);
    setShowLanding(false);
  }, []);

  const activeId = snapshot.activeId;
  const active = snapshot.sessions.find((s) => s.meta.channelId === activeId) ?? snapshot.sessions[0] ?? null;
  const hasSessions = snapshot.sessions.length > 0;

  // Desktop multi-tab convenience: reflect the active session name and total unread count
  // in the browser tab title, so a backgrounded Weft tab is distinguishable at a glance.
  // Mobile PWA installs also get this (harmless there — no tab strip to read it from).
  useEffect(() => {
    const unreadCount = snapshot.sessions.reduce((sum, s) => sum + (s.unread ? 1 : 0), 0);
    const prefix = unreadCount > 0 ? `(${unreadCount}) ` : '';
    document.title = active ? `${prefix}${active.meta.title} · Weft` : 'Weft';
    return () => {
      document.title = 'Weft';
    };
  }, [active, snapshot.sessions]);

  if (!snapshot.ready) {
    return (
      <main className="boot">
        <div className="boot-mark" aria-hidden="true">W</div>
        <p>Restoring your sessions…</p>
      </main>
    );
  }

  // Explicit "join another session" from within the app.
  if (adding) {
    return (
      <ConnectScreen
        hasSessions={hasSessions}
        initialManual={addManual}
        error={error}
        onError={setError}
        onPair={handlePair}
        onCancel={() => {
          setError(null);
          setAddManual(false);
          setAdding(false);
        }}
        sessions={snapshot.sessions}
        activeId={activeId}
        devices={snapshot.devices}
        onSelectSession={(id) => {
          setAdding(false);
          sessionRuntime.setActive(id);
        }}
        onStartOnDevice={(id) => {
          setAdding(false);
          openStart(id);
        }}
        onOpenDeviceDetails={(id) => {
          setAdding(false);
          openDeviceDetails(id);
        }}
        onOpenDevices={() => {
          setAdding(false);
          openDevices();
        }}
        onRemoveSession={removeSession}
        onRenameSession={(id, title) => sessionRuntime.renameSession(id, title)}
        onGoHome={() => {
          setAdding(false);
          setError(null);
          setShowLanding(true);
        }}
      />
    );
  }

  // Explicit "Home" tap from the session screen: show the landing page even though
  // sessions exist, with links back into them.
  if (showLanding && hasSessions && active) {
    return (
      <LandingScreen
        hasSessions
        onOpenSessions={() => {
          setError(null);
          setShowLanding(false);
        }}
        onBeginPair={(manual) => {
          setError(null);
          setShowLanding(false);
          openJoin(!!manual);
        }}
        onStartDemo={handleDemo}
        onStartSession={() => openStart()}
        error={error}
        onError={setError}
      />
    );
  }

  // Full "connected devices" manager: every registered listener, live status, and per-device
  // actions — distinct from StartSessionScreen (launching ONE session) and ConnectScreen
  // (mirroring an existing session by QR).
  if (devicesOpen) {
    return (
      <Suspense fallback={loadingScreen('Opening your devices…')}>
        <DevicesScreen
          sessions={snapshot.sessions}
          activeId={activeId}
          devices={snapshot.devices}
          onRefreshProjects={(id) => void sessionRuntime.refreshProjects(id)}
          onSetDefault={(id) => sessionRuntime.setDefaultDevice(id)}
          onForget={(id) => sessionRuntime.forgetDevice(id)}
          onStartOnDevice={(id) => openStart(id)}
          onOpenDetails={(id) => openDeviceDetails(id)}
          onScanListener={() => openJoin(false)}
          onSelectSession={(id) => {
            closeDeviceScreens();
            sessionRuntime.setActive(id);
          }}
          onAddSession={() => openJoin(false)}
          onStartSession={() => openStart()}
          onOpenDevices={openDevices}
          onRemoveSession={removeSession}
          onRenameSession={(id, title) => sessionRuntime.renameSession(id, title)}
          onGoHome={() => {
            closeDeviceScreens();
            setError(null);
            setShowLanding(true);
          }}
        />
      </Suspense>
    );
  }

  if (terminalChannelId) {
    const device = snapshot.devices.find((d) => d.channelId === terminalChannelId);
    if (device) {
      return (
        <Suspense fallback={loadingScreen('Opening terminal…')}>
          <TerminalScreen
            device={device}
            controller={sessionRuntime.terminal(terminalChannelId)}
            onBack={() => window.history.back()}
            onReconnect={() => void sessionRuntime.connectDevice(terminalChannelId)}
          />
        </Suspense>
      );
    }
  }

  // Single-device drill-down: live status, event log, and every session ever spawned from this
  // device (matched by its stable deviceId, so it survives weft restarts).
  if (deviceDetailsChannelId) {
    const device = snapshot.devices.find((d) => d.channelId === deviceDetailsChannelId);
    if (device) {
      return (
        <Suspense fallback={loadingScreen('Opening device details…')}>
          <DeviceDetailsScreen
            device={device}
            activeId={activeId}
            sessions={snapshot.sessions}
            devices={snapshot.devices}
            onRefreshProjects={(id) => void sessionRuntime.refreshProjects(id)}
            onStartMonitoring={startDeviceMonitoring}
            onOpenTerminal={openTerminal}
            onStopMonitoring={stopDeviceMonitoring}
            onOpenDeviceClipboard={openDeviceClipboard}
            onCloseDeviceClipboard={closeDeviceClipboard}
            onReadDeviceClipboard={readDeviceClipboard}
            onWriteDeviceClipboard={writeDeviceClipboard}
            onRefreshDeviceKeepAwake={refreshDeviceKeepAwake}
            onStartDeviceKeepAwake={startDeviceKeepAwake}
            onStopDeviceKeepAwake={stopDeviceKeepAwake}
            onResumeOnDevice={(id) => openStart(id, 'resume')}
            onSetDefault={(id) => sessionRuntime.setDefaultDevice(id)}
            onForget={async (id) => {
              await sessionRuntime.forgetDevice(id);
              closeDeviceScreens();
            }}
            onStartOnDevice={(id) => openStart(id)}
            onOpenDeviceDetails={(id) => openDeviceDetails(id)}
            onJoinOffer={(deviceId, offerChannelId) => {
              closeDeviceScreens();
              void sessionAccess.open({
                deviceChannelId: deviceId,
                target: { kind: 'offer', offerChannelId },
              }).then((status) => {
                if (status.state === 'failed') setError(lifecycleFailureMessage(status.failure));
              });
            }}
            onOpenSession={(id) => {
              closeDeviceScreens();
              sessionRuntime.setActive(id);
            }}
            onSelectSession={(id) => {
              closeDeviceScreens();
              sessionRuntime.setActive(id);
            }}
            onAddSession={() => openJoin(false)}
            onStartSession={() => openStart()}
            onOpenDevices={openDevices}
            onRemoveSession={removeSession}
            onRenameSession={(id, title) => sessionRuntime.renameSession(id, title)}
            onGoHome={() => {
              closeDeviceScreens();
              setError(null);
              setShowLanding(true);
            }}
          />
        </Suspense>
      );
    }
    // Device vanished (forgotten elsewhere) — fall through to the normal screen below.
  }

  // Also cover the "device paired but no session started yet" case: rather than falling through
  // to the plain marketing landing page (which would look like nothing happened), go straight to
  // the device-aware start flow so the paired device is visible and ready to launch a session on.
  const hasDevicesOnly = !hasSessions && snapshot.devices.length > 0;

  if (starting || hasDevicesOnly) {
    return (
      <Suspense fallback={loadingScreen('Loading session controls…')}>
        <StartSessionScreen
          hasSessions={hasSessions}
          devices={snapshot.devices}
          initialChannelId={startDeviceId}
          initialMode={startMode}
          onConnectDevice={(id) => void sessionRuntime.connectDevice(id)}
          onOpen={async (intent) => {
            const status = await sessionAccess.open(intent);
            if (
              status.state !== 'failed' &&
              status.state !== 'cancelled' &&
              !requiresTakeoverConfirmation(status)
            ) {
              setStarting(false);
              setStartDeviceId(undefined);
              setShowLanding(false);
            }
            return status;
          }}
          onConfirmTakeover={async (operationId) => {
            const status = await sessionAccess.confirmTakeover(operationId);
            if (
              status.state !== 'failed' &&
              status.state !== 'cancelled' &&
              !requiresTakeoverConfirmation(status)
            ) {
              setStarting(false);
              setStartDeviceId(undefined);
              setShowLanding(false);
            }
            return status;
          }}
          onRefreshSessions={(id, cwd) => void sessionRuntime.refreshSessions(id, cwd)}
          onScanListener={() => openJoin(false)}
          onManageDevices={openDevices}
          onCancel={() => {
            setStarting(false);
            setStartDeviceId(undefined);
            setError(null);
          }}
          sessions={snapshot.sessions}
          activeId={activeId}
          onSelectSession={(id) => {
            setStarting(false);
            setStartDeviceId(undefined);
            sessionRuntime.setActive(id);
          }}
          onRemoveSession={removeSession}
          onRenameSession={(id, title) => sessionRuntime.renameSession(id, title)}
          onGoHome={() => {
            setStarting(false);
            setStartDeviceId(undefined);
            setError(null);
            setShowLanding(true);
          }}
        />
      </Suspense>
    );
  }

  // True first run (no sessions, no paired devices either): web shows the onboarding landing;
  // the native app skips marketing and goes straight to the scan/pair screen.
  if (!hasSessions || !active) {
    return isNativeRuntime() ? (
      <ConnectScreen
        firstRun
        hasSessions={false}
        error={error}
        onError={setError}
        onPair={handlePair}
      />
    ) : (
      <LandingScreen
        onBeginPair={(manual) => {
          openJoin(!!manual);
        }}
        onStartDemo={handleDemo}
        onStartSession={() => openStart()}
        error={error}
        onError={setError}
      />
    );
  }

  const sessionScreen = (
    <SessionScreen
      active={active}
      sessions={snapshot.sessions}
      activeId={active.meta.channelId}
      onPrompt={(text, attachments, delivery?: PromptDelivery) =>
        void sessionRuntime.sendPrompt(active.meta.channelId, text, attachments, delivery)
      }
      onApprove={(requestId, optionId) => void sessionRuntime.sendApproval(active.meta.channelId, requestId, optionId)}
      onElicitationRespond={(requestId, action, content) =>
        void sessionRuntime.sendElicitation(active.meta.channelId, requestId, action, content)
      }
      onInterrupt={() => void sessionRuntime.sendInterrupt(active.meta.channelId)}
      onModeChange={(mode: SessionMode) => void sessionRuntime.sendMode(active.meta.channelId, mode)}
      onCommand={(name, input) => void sessionRuntime.sendCommand(active.meta.channelId, name, input)}
      onRetry={(itemId) => void sessionRuntime.retryPrompt(active.meta.channelId, itemId)}
      onSelectSession={(id) => sessionRuntime.setActive(id)}
      onAddSession={() => {
        setAddManual(false);
        setAdding(true);
      }}
      onOpenExplore={openExplore}
      onOpenDiscover={openDiscoverFromChat}
      exploreOpen={exploreOpen}
      exploreInitialView={exploreEntry === 'direct-discover' ? 'discover' : undefined}
      exploreDirectFromChat={exploreEntry === 'direct-discover'}
      onCloseExplore={closeExplore}
      onStartSession={() => openStart()}
      onOpenDevices={openDevices}
      devices={snapshot.devices}
      onStartOnDevice={(id) => openStart(id)}
      onOpenDeviceDetails={(id) => openDeviceDetails(id)}
      onVoiceModeChange={handleVoiceModeChange}
      onRemoveSession={removeSession}
      onRenameSession={(id, title) => sessionRuntime.renameSession(id, title)}
      onPinSession={(id, pinned) => void sessionRuntime.pin(id, pinned)}
      onReloadHistory={(id) => sessionRuntime.reloadHistory(id)}
      onArchiveSession={(id) => sessionRuntime.archive(id)}
      onReconnect={(id) => void sessionRuntime.reconnect(id)}
      onRetrySpawn={(id) => {
        const operationId = snapshot.sessions.find((session) => session.meta.channelId === id)
          ?.spawning?.requestId;
        if (operationId) void sessionAccess.retry(operationId);
      }}
      onGoHome={() => {
        if (exploreOpen) {
          closeExplore();
          setExploreOpen(false);
        }
        setError(null);
        setShowLanding(true);
      }}
      onLoadEarlier={() => {}}
    />
  );

  return (
    <Suspense fallback={loadingScreen('Opening your session…')}>
      {sessionScreen}
    </Suspense>
  );
}
